import { Component, Input, OnChanges, TemplateRef, ViewChild } from '@angular/core';
import { IVTableColumn } from '@app/directives/_layout/v-table/_models/IVTableColumn';
import { IVTableRow } from '@app/directives/_layout/v-table/_models/IVTableRow';
import { VTableService } from '@app/directives/_layout/v-table/_services/v-table.service';
import { TemperatureReadingComponent } from '@app/directives/_misc/temperatureReading/temperatureReading.component';
import { SortType } from '@app/enum/SortType';
import { IPlotTemperatureRecord } from '@interfaces/IPlot';
import { DateService } from '@services/date.service';
import { TranslationService } from '@services/translation.service';
import { UnitConversionType } from '@services/unitConversion.service';
import { VehicleService } from '@services/vehicle.service';
import { ITemperatureSetpointWithHistories } from '@temperature/_components/tempGraph/temperature-graph.component';
import { TemperatureProbeCategory } from '@temperature/_enums/temperature/TemperatureProbeCategory.enum';
import { IMultiStateInputWithHistories } from '@temperature/_models/multiStateInputs/IMultiStateInput';
import { IZoneWithProbes } from '@temperature/_models/temperature/ITemperatureZone';
import { IZoneTemperatureOutOfBoundsHistory } from '@temperature/_models/temperature/ITemperatureZoneOutOfBoundsHistory';
import { MultiStateInputService } from '@temperature/_services/multi-state-input.service';
import { ReeferService } from '@temperature/_services/reefer.service';
import { TemperatureProbeService } from '@temperature/_services/temperature-probe.service';
import { TemperatureSetpointService } from '@temperature/_services/temperature-setpoint.service';
import { ZoneService } from '@temperature/_services/zone.service';
import { lastValueFrom } from 'rxjs';
import { ZoneOperatingModeWidget } from '../zone/zoneOperatingModeWidget/zone-operating-mode-widget.component';
import './temperature-source-data.component.scss';

@Component({
    selector: 'temp-source-data',
    template: require('./temperature-source-data.component.html')
})
export class TemperatureSourceDataComponent implements OnChanges {

    @Input() zone: IZoneWithProbes;

    @Input() startDate: Date;
    @Input() endDate: Date;

    @ViewChild('outOfBounds') outOfBoundsTemplate: TemplateRef<any>;

    loading: boolean;

    sourceData: any[];

    tableColumns: IVTableColumn<any>[];
    tableRows: IVTableRow[];
    // ---

    temperatureRecords: IPlotTemperatureRecord[];
    temperatureSetpoints: ITemperatureSetpointWithHistories[];
    multiStateInputs: IMultiStateInputWithHistories[];
    zoneTemperatureOutOfBoundsHistory: IZoneTemperatureOutOfBoundsHistory[];

    UnitConversionType = UnitConversionType;

    constructor(
        private readonly dateService: DateService,
        private readonly multiStateInputService: MultiStateInputService,
        private readonly reeferService: ReeferService,
        private readonly temperatureProbeService: TemperatureProbeService,
        private readonly temperatureSetpointService: TemperatureSetpointService,
        private readonly translationService: TranslationService,
        private readonly vehicleService: VehicleService,
        private readonly vTableService: VTableService,
        private readonly zoneService: ZoneService) {
    }

    ngOnInit(): void {
        this.createTable();
    }

    ngOnChanges() {
        this.getData();
    }

    showProbeData(): void {
        if (!this.zone || !this.zone.probes) {
            return;
        }

        this.sourceData = [];

        this.zone.probes.forEach(probe => {
            probe.records.forEach(record => {
                var tableRecord = this.sourceData.find(h => h.timestamp === record.Timestamp)
                if (!tableRecord) {
                    tableRecord = {
                        timestamp: record.Timestamp,
                        operatingMode: this.reeferService.getOperatingModeForTemperatureRecord(record, this.multiStateInputs, this.zone.Id)?.TriggerState,
                        setpoint: this.temperatureSetpointService.getSetpointForTemperatureRecord(record, this.temperatureSetpoints, this.zone.Id)?.SetpointValue,
                        outOfBounds: false,
                        plotId: record.PlotId
                    }

                    this.sourceData.push(tableRecord);
                }

                switch (probe.Category) {
                    case TemperatureProbeCategory.Return: {
                        tableRecord.return = record.Temperature;
                        break;
                    }
                    case TemperatureProbeCategory.Supply: {
                        tableRecord.supply = record.Temperature;
                        break;
                    }
                    case TemperatureProbeCategory.Evaporator: {
                        tableRecord.evaporator = record.Temperature;
                        break;
                    }
                }

               tableRecord.outOfBounds = this.getZoneOutOfBounds(tableRecord);
            })
        })
    }

    //--

    private getZoneOutOfBounds(tableRecord: any): boolean {
        if (this.tableRecordHasOutOfBoundsHistory(tableRecord)) {
            return true;
        }
    }

    private tableRecordHasOutOfBoundsHistory(tableRecord: any): boolean {
        if (!this.zoneTemperatureOutOfBoundsHistory) {
            return false;
        }

        return this.zoneTemperatureOutOfBoundsHistory.some(history => {
            return this.dateService.date1IsAfterDate2(tableRecord.timestamp, history.StartTime) && (this.dateService.date1IsAfterDate2(history.EndTime, tableRecord.timestamp) || !history.EndTime);
        })
    }

    private getData(): void {
        this.loading = true;
        const promises = [];
        promises.push(this.vehicleService.getVehicleTemperatureRecords(this.zone.VehicleId, this.startDate, this.endDate));
        promises.push(this.temperatureSetpointService.loadSetpoints(this.zone.VehicleId, this.startDate, this.endDate));
        promises.push(lastValueFrom(this.multiStateInputService.getMultiStateInputs({ VehicleId: this.zone.VehicleId })));
        promises.push(lastValueFrom(this.multiStateInputService.getMultiStateInputHistories({ VehicleId: this.zone.VehicleId, Start: this.startDate, End: this.endDate })));
        promises.push(lastValueFrom(this.zoneService.getZonesTemperatureOutOfBoundsHistory({ ZoneId: this.zone.Id })));

        Promise.all(promises).then(([temperatureRecords, setpoints, multiStateInputs, multiStateInputHistories, outOfBoundsHistory]) => {
            this.temperatureRecords = temperatureRecords;
            this.temperatureSetpoints = setpoints;

            this.multiStateInputs = multiStateInputs;
            this.multiStateInputService.mapHistoriesToInputs(this.multiStateInputs, multiStateInputHistories);
            this.temperatureProbeService.mapTemperatureRecordsToProbes(this.zone.probes, this.temperatureRecords);
            this.zoneTemperatureOutOfBoundsHistory = outOfBoundsHistory;
        }).finally(() => {
            this.showProbeData();
            this.tableRows = this.vTableService.createTableRows(this.sourceData, this.tableColumns);
            this.loading = false;
        })
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createDateTableHeader({sorted: true}),
                createCellFunction: (item) => this.vTableService.createDateCell(item.timestamp)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "ReeferDetail.Mode", width: 80 }),
                createCellFunction: (item) => this.vTableService.createTableCell(
                    item.operatingMode,
                    {
                        classes: "text-center",
                        component: ZoneOperatingModeWidget,
                        componentInputs: { category: item.operatingMode }
                    })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'ReeferList.SetPoint', width: 90, sortType: SortType.Number }),
                createCellFunction: (item) => this.vTableService.createTableCell(
                    item.setpoint,
                    {
                        component: TemperatureReadingComponent,
                        componentInputs: { temperatureReading: item.setpoint }
                    })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'ReeferList.Return', width: 90, sortType: SortType.Number }),
                createCellFunction: (item) => this.vTableService.createTableCell(
                    item.return,
                    {
                        component: TemperatureReadingComponent,
                        componentInputs: { temperatureReading: item.return }
                    })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'ReeferList.Supply', width: 90, sortType: SortType.Number }),
                createCellFunction: (item) => this.vTableService.createTableCell(
                    item.supply,
                    {
                        component: TemperatureReadingComponent,
                        componentInputs: { temperatureReading: item.supply }
                    })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'ReeferList.Evaporator', width: 100, sortType: SortType.Number }),
                createCellFunction: (item) => this.vTableService.createTableCell(
                    item.evaporator,
                    {
                        component: TemperatureReadingComponent,
                        componentInputs: { temperatureReading: item.evaporator }
                    })
            },
            {
                header: this.vTableService.createLocationInformationHeader(),
                createCellFunction: (item) => this.vTableService.createLocationInformationCell({ plotId: item.plotId })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'Common.Notes' }),
                createCellFunction: (item) => this.vTableService.createTableTemplateCell(item.outOfBounds, this.outOfBoundsTemplate)
            }
        ]
    }
}
